import { computed, ref, watch } from 'vue'
import {
  MONTH_LABELS_FR,
  formatPeriodLabel,
  normalizePeriods,
  parsePeriod
} from '@shared/formation-periods.js'

/**
 * Composable pour sélectionner des mois entiers ("YYYY-MM") comme période
 * approximative de formation, quand la date exacte n'est pas encore connue.
 * Pendant du composable useCalendar, à l'échelle du mois plutôt que du jour.
 *
 * @param {import('vue').Ref<string[]>} selectedPeriods - Périodes sélectionnées (writable computed côté appelant)
 * @param {Date} [minDate] - Premier mois sélectionnable (par défaut : le mois courant)
 * @returns {Object} État et actions de la grille de mois
 */
export function useMonthPeriods(selectedPeriods, minDate = null) {
  const reference = minDate || new Date()
  const minYear = reference.getFullYear()
  const minMonth = reference.getMonth()

  // Année de la première période sélectionnée : recalculée à chaque changement
  // pour que la grille suive les périodes rechargées (réouverture de la modale).
  const firstSelectedYear = computed(() => {
    const [first] = normalizePeriods(selectedPeriods.value)
    return first ? parsePeriod(first).year : null
  })

  const displayedYear = ref(Math.max(firstSelectedYear.value ?? minYear, minYear))

  watch(firstSelectedYear, (year) => {
    if (year !== null) displayedYear.value = Math.max(year, minYear)
  })

  const toPeriod = (year, monthIndex) => `${year}-${String(monthIndex + 1).padStart(2, '0')}`

  const isPeriodSelected = (period) => selectedPeriods.value.includes(period)

  const months = computed(() =>
    MONTH_LABELS_FR.map((label, index) => {
      const period = toPeriod(displayedYear.value, index)
      return {
        label,
        shortLabel: label.slice(0, 3),
        period,
        // Un mois passé n'est pas ajoutable, mais reste décochable s'il est déjà sélectionné
        disabled:
          displayedYear.value === minYear && index < minMonth && !isPeriodSelected(period)
      }
    })
  )

  const togglePeriod = (period) => {
    if (isPeriodSelected(period)) {
      removePeriod(period)
      return
    }
    // Nouveau tableau pour déclencher le setter du computed
    selectedPeriods.value = normalizePeriods([...selectedPeriods.value, period])
  }

  const removePeriod = (period) => {
    selectedPeriods.value = selectedPeriods.value.filter((p) => p !== period)
  }

  const isPreviousYearDisabled = computed(() => displayedYear.value <= minYear)

  const previousYear = () => {
    if (!isPreviousYearDisabled.value) displayedYear.value--
  }

  const nextYear = () => {
    displayedYear.value++
  }

  const sortedSelectedPeriods = computed(() => normalizePeriods(selectedPeriods.value))

  return {
    displayedYear,
    months,
    isPeriodSelected,
    togglePeriod,
    removePeriod,
    previousYear,
    nextYear,
    isPreviousYearDisabled,
    sortedSelectedPeriods,
    formatPeriodLabel
  }
}
